import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import toast, { Toaster } from 'react-hot-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Upload, FileText, Brain, CheckCircle, AlertCircle, Loader2,
  ChevronLeft, Eye, X, Edit2, Save, XCircle, Sparkles,
  Table, Shield, Search, Download, HelpCircle, Keyboard,
  Zap, TrendingUp, Clock, FileCheck
} from 'lucide-react';

const DocumentIntelligencePage = () => {
  const navigate = useNavigate();
  
  // State management
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [document, setDocument] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  
  // Refs
  const fileInputRef = useRef(null);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ctrl/Cmd + U: Upload
      if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
        e.preventDefault();
        fileInputRef.current?.click();
        toast('📤 Upload shortcut activated', { icon: '⌨️' });
      }
      // Ctrl/Cmd + N: New document
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        setDocument(null);
        setSuggestions([]);
        setSelectedFile(null);
        setPreviewUrl(null);
        toast.success('Ready for new document');
      }
      // Ctrl/Cmd + S: Save (when editing)
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && isEditing) {
        e.preventDefault();
        handleSaveEdits();
      }
      // Ctrl/Cmd + Z: Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey && undoStack.length > 0) {
        e.preventDefault();
        handleUndo();
      }
      // Ctrl/Cmd + Shift + Z: Redo
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z' && redoStack.length > 0) {
        e.preventDefault();
        handleRedo();
      }
      // ?: Show keyboard shortcuts
      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        toast((t) => (
          <div className="text-sm">
            <div className="font-bold mb-2">⌨️ Keyboard Shortcuts</div>
            <div>Ctrl+U: Upload document</div>
            <div>Ctrl+N: New document</div>
            <div>Ctrl+S: Save edits</div>
            <div>Ctrl+Z: Undo</div>
            <div>Ctrl+Shift+Z: Redo</div>
            <div>?: Show this help</div>
          </div>
        ), { duration: 5000 });
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isEditing, undoStack, redoStack]);
  
  // Check if first time user
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('doc_intel_onboarding_seen');
    if (!hasSeenOnboarding) {
      setShowOnboarding(true);
    }
  }, []);
  
  // Load analytics, templates, history
  useEffect(() => {
    loadAnalytics();
    loadTemplates();
    loadHistory();
  }, []);
  
  const loadAnalytics = async () => {
    try {
      const response = await fetch(`${API}/documents/analytics`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };
  
  const loadTemplates = async () => {
    try {
      const response = await fetch(`${API}/documents/templates`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };
  
  const loadHistory = async () => {
    try {
      const response = await fetch(`${API}/documents/history?limit=20`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };
  
  // Dropzone configuration
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
      
      toast.success(`📄 ${file.name} ready to process`);
    }
  }, []);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.gif']
    },
    maxSize: 10485760, // 10MB
    multiple: false
  });
  
  // Upload and analyze document
  const handleConfirmUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);
      
      const response = await fetch(`${API}/documents/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (response.ok) {
        const data = await response.json();
        setDocument(data);
        setEditedData(data.extracted_data?.entities || {});
        
        // Load suggestions
        await loadSuggestions(data.document_id);
        
        // Clear preview
        setSelectedFile(null);
        setPreviewUrl(null);
        setUploadProgress(0);
        
        toast.success('✨ Document analyzed successfully!', {
          icon: '🎉',
          duration: 3000
        });
        
        // Reload analytics and history
        loadAnalytics();
        loadHistory();
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to analyze document. Please try again.');
    } finally {
      setUploading(false);
    }
  };
  
  const loadSuggestions = async (documentId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/documents/${documentId}/suggestions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (error) {
      console.error('Failed to load suggestions:', error);
      toast.error('Failed to load automation suggestions');
    } finally {
      setLoading(false);
    }
  };
  
  // Edit handlers
  const handleStartEdit = () => {
    // Save to undo stack
    setUndoStack([...undoStack, { ...editedData }]);
    setRedoStack([]);
    setIsEditing(true);
    toast('✏️ Editing mode activated', { icon: '📝' });
  };
  
  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
    
    // Auto-save after 3 seconds of inactivity
    if (autoSaveTimer) clearTimeout(autoSaveTimer);
    
    const timer = setTimeout(() => {
      handleSoftSave();
    }, 3000);
    
    setAutoSaveTimer(timer);
  };
  
  const handleSoftSave = async () => {
    try {
      await fetch(`${API}/documents/${document.document_id}/soft-save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ entities: editedData })
      });
      
      toast.success('💾 Auto-saved', { duration: 1000, icon: '✓' });
    } catch (error) {
      console.error('Soft save error:', error);
    }
  };
  
  const handleSaveEdits = async () => {
    try {
      const response = await fetch(`${API}/documents/${document.document_id}/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ entities: editedData })
      });
      
      if (response.ok) {
        const updatedDoc = await response.json();
        setDocument(updatedDoc);
        setIsEditing(false);
        
        // Clear auto-save timer
        if (autoSaveTimer) clearTimeout(autoSaveTimer);
        
        // Reload suggestions with updated data
        await loadSuggestions(document.document_id);
        
        toast.success('✅ Changes saved successfully!', {
          icon: '🎉',
          duration: 2000
        });
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save changes');
    }
  };
  
  const handleCancelEdit = () => {
    setEditedData(document.extracted_data?.entities || {});
    setIsEditing(false);
    if (autoSaveTimer) clearTimeout(autoSaveTimer);
    toast('❌ Changes discarded', { icon: '🔙' });
  };
  
  const handleUndo = () => {
    if (undoStack.length === 0) return;
    
    const previous = undoStack[undoStack.length - 1];
    setRedoStack([...redoStack, { ...editedData }]);
    setUndoStack(undoStack.slice(0, -1));
    setEditedData(previous);
    toast('↩️ Undo', { duration: 1000 });
  };
  
  const handleRedo = () => {
    if (redoStack.length === 0) return;
    
    const next = redoStack[redoStack.length - 1];
    setUndoStack([...undoStack, { ...editedData }]);
    setRedoStack(redoStack.slice(0, -1));
    setEditedData(next);
    toast('↪️ Redo', { duration: 1000 });
  };
  
  // Create workflow from suggestion
  const handleCreateWorkflow = async (suggestion) => {
    try {
      const response = await fetch(`${API}/documents/${document.document_id}/create-workflow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ suggestion_id: suggestion.id })
      });
      
      if (response.ok) {
        const workflow = await response.json();
        toast.success('🎉 Workflow created successfully!', {
          duration: 3000,
          icon: '✨'
        });
        
        // Navigate to workflow editor
        setTimeout(() => {
          navigate(`/workflows/${workflow.workflow_id}/edit`);
        }, 1500);
      }
    } catch (error) {
      console.error('Workflow creation error:', error);
      toast.error('Failed to create workflow');
    }
  };
  
  // Onboarding
  const handleCompleteOnboarding = () => {
    localStorage.setItem('doc_intel_onboarding_seen', 'true');
    setShowOnboarding(false);
    toast.success('🎓 You\'re all set! Start uploading documents.');
  };
  
  // Filtered history based on search
  const filteredHistory = history.filter(doc =>
    doc.filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.document_type?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 100 }
    }
  };
  
  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <Toaster position="top-right" />
      
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between mb-6"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Document Intelligence
            </h1>
            <p className="text-sm text-gray-500">AI-powered document analysis and automation</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => toast((t) => (
              <div className="text-sm">
                <div className="font-bold mb-2">⌨️ Keyboard Shortcuts</div>
                <div>Ctrl+U: Upload</div>
                <div>Ctrl+N: New</div>
                <div>Ctrl+S: Save</div>
                <div>Ctrl+Z: Undo</div>
                <div>?: Help</div>
              </div>
            ), { duration: 5000 })}
          >
            <Keyboard className="w-4 h-4 mr-2" />
            Shortcuts
          </Button>
          
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </motion.div>
      
      {/* Onboarding Tour */}
      <AnimatePresence>
        {showOnboarding && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={handleCompleteOnboarding}
          >
            <Card className="max-w-md" onClick={(e) => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-yellow-500" />
                  Welcome to Document Intelligence!
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                  Upload any document (invoice, receipt, contract, etc.) and our AI will:
                </p>
                <ul className="text-sm text-gray-600 space-y-2 list-disc list-inside">
                  <li>Extract all data automatically</li>
                  <li>Suggest relevant automations</li>
                  <li>Create workflows with one click</li>
                  <li>Save you 60x time</li>
                </ul>
                <Button onClick={handleCompleteOnboarding} className="w-full">
                  Got it! Let's start
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Main Content */}
      <Tabs defaultValue="upload" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="upload">
            <Upload className="w-4 h-4 mr-2" />
            Upload
          </TabsTrigger>
          <TabsTrigger value="templates">
            <FileCheck className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <TrendingUp className="w-4 h-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="history">
            <Clock className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
          <TabsTrigger value="advanced">
            <Zap className="w-4 h-4 mr-2" />
            Advanced
          </TabsTrigger>
        </TabsList>
        
        {/* Upload Tab */}
        <TabsContent value="upload">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {/* Upload Section */}
            <motion.div variants={itemVariants}>
              <Card>
                <CardHeader>
                  <CardTitle>Upload Document</CardTitle>
                  <CardDescription>
                    Drag & drop or click to upload (PDF, JPEG, PNG, WebP, GIF)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!selectedFile && !document && (
                    <motion.div
                      {...getRootProps()}
                      className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all ${
                        isDragActive
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <input {...getInputProps()} ref={fileInputRef} />
                      <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      {isDragActive ? (
                        <p className="text-blue-600 font-medium">Drop the file here...</p>
                      ) : (
                        <>
                          <p className="text-gray-600 font-medium mb-2">
                            Drag & drop your document here
                          </p>
                          <p className="text-sm text-gray-400">or click to browse</p>
                          <p className="text-xs text-gray-400 mt-2">Max file size: 10MB</p>
                        </>
                      )}
                    </motion.div>
                  )}
                  
                  {/* Preview */}
                  {selectedFile && previewUrl && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-4"
                    >
                      <div className="relative border rounded-lg p-4 bg-gray-50">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => {
                            setSelectedFile(null);
                            setPreviewUrl(null);
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        
                        {selectedFile.type.startsWith('image/') ? (
                          <img
                            src={previewUrl}
                            alt="Preview"
                            className="max-h-64 mx-auto rounded"
                          />
                        ) : (
                          <div className="text-center py-8">
                            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-2" />
                            <p className="text-sm font-medium">{selectedFile.name}</p>
                            <p className="text-xs text-gray-500">
                              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <Button
                        onClick={handleConfirmUpload}
                        disabled={uploading}
                        className="w-full"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing... {uploadProgress}%
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Confirm & Process
                          </>
                        )}
                      </Button>
                      
                      {uploading && (
                        <Progress value={uploadProgress} className="w-full" />
                      )}
                    </motion.div>
                  )}
                  
                  {/* Extracted Data */}
                  {document && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="space-y-4"
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Extracted Data
                        </h3>
                        <div className="flex items-center gap-2">
                          {isEditing ? (
                            <>
                              <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                                <XCircle className="w-4 h-4 mr-1" />
                                Cancel
                              </Button>
                              <Button size="sm" onClick={handleSaveEdits}>
                                <Save className="w-4 h-4 mr-1" />
                                Save
                              </Button>
                            </>
                          ) : (
                            <Button size="sm" variant="outline" onClick={handleStartEdit}>
                              <Edit2 className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Badge variant="outline">
                          {document.extracted_data?.document_type || 'Unknown'}
                        </Badge>
                        <Badge variant={document.extracted_data?.extraction_method === 'gpt4_vision' ? 'default' : 'secondary'}>
                          {document.extracted_data?.extraction_method === 'gpt4_vision' ? 'GPT-4 Vision' : 'OCR'}
                        </Badge>
                        <div className="flex items-center gap-1">
                          <span>Confidence:</span>
                          <Progress
                            value={document.extracted_data?.confidence || 0}
                            className="w-16 h-2"
                          />
                          <span>{document.extracted_data?.confidence || 0}%</span>
                        </div>
                      </div>
                      
                      {isEditing && (
                        <p className="text-xs text-gray-500">
                          💾 Auto-saves every 3 seconds
                        </p>
                      )}
                      
                      <ScrollArea className="h-64 border rounded-lg p-4">
                        <div className="space-y-2">
                          {Object.entries(editedData).map(([key, value]) => (
                            <div key={key} className="flex items-start gap-2">
                              <span className="text-sm font-medium text-gray-600 min-w-32">
                                {key.replace(/_/g, ' ').toUpperCase()}:
                              </span>
                              {isEditing ? (
                                <Input
                                  value={value || ''}
                                  onChange={(e) => handleFieldChange(key, e.target.value)}
                                  className="flex-1 h-8 text-sm"
                                />
                              ) : (
                                <span className="text-sm">{value || 'N/A'}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            // Extract tables
                            toast.promise(
                              fetch(`${API}/documents/${document.document_id}/extract-tables`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                              }).then(r => r.json()),
                              {
                                loading: 'Extracting tables...',
                                success: (data) => `Found ${data.table_count} table(s)!`,
                                error: 'Failed to extract tables'
                              }
                            );
                          }}
                        >
                          <Table className="w-4 h-4 mr-1" />
                          Extract Tables
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            // Verify ID
                            toast.promise(
                              fetch(`${API}/documents/${document.document_id}/verify-id`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                              }).then(r => r.json()),
                              {
                                loading: 'Verifying ID...',
                                success: (data) => `Verification: ${data.verification_status}`,
                                error: 'Failed to verify ID'
                              }
                            );
                          }}
                        >
                          <Shield className="w-4 h-4 mr-1" />
                          Verify ID
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
            
            {/* Suggestions Section */}
            <motion.div variants={itemVariants}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-yellow-500" />
                    AI Suggestions
                  </CardTitle>
                  <CardDescription>
                    Recommended automations based on your document
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!document ? (
                    <div className="text-center py-12 text-gray-400">
                      <Brain className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>Upload a document to see AI suggestions</p>
                    </div>
                  ) : loading ? (
                    <div className="text-center py-12">
                      <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-500 mb-4" />
                      <p className="text-sm text-gray-500">Generating suggestions...</p>
                    </div>
                  ) : suggestions.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <AlertCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>No suggestions available</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-96">
                      <div className="space-y-4">
                        {suggestions.map((suggestion, index) => (
                          <motion.div
                            key={suggestion.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                          >
                            <Card className="border-2 hover:border-blue-300 transition-colors">
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between mb-2">
                                  <h4 className="font-semibold">{suggestion.title}</h4>
                                  <Badge variant="secondary">{suggestion.category}</Badge>
                                </div>
                                <p className="text-sm text-gray-600 mb-3">
                                  {suggestion.description}
                                </p>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <Badge variant="outline">
                                      {suggestion.mapped_fields_count} fields mapped
                                    </Badge>
                                    <span>Confidence: {suggestion.confidence}%</span>
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => handleCreateWorkflow(suggestion)}
                                  >
                                    <Zap className="w-4 h-4 mr-1" />
                                    Create Workflow
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          </motion.div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </TabsContent>
        
        {/* Templates Tab */}
        <TabsContent value="templates">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Card>
              <CardHeader>
                <CardTitle>Document Templates</CardTitle>
                <CardDescription>
                  Pre-built templates for common document types
                </CardDescription>
              </CardHeader>
              <CardContent>
                {templates.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Loading templates...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {templates.map((template, index) => (
                      <motion.div
                        key={template.template_id || template.name}
                        variants={itemVariants}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-blue-500">
                          <CardContent className="p-5">
                            <div className="flex items-start gap-3 mb-3">
                              <div className="p-3 bg-gradient-to-br from-blue-100 to-blue-50 rounded-lg">
                                <FileCheck className="w-6 h-6 text-blue-600" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-bold text-lg mb-1">{template.name}</h4>
                                <Badge variant="outline" className="text-xs">
                                  {template.category}
                                </Badge>
                              </div>
                            </div>
                            
                            <p className="text-sm text-gray-600 mb-3">
                              {template.description}
                            </p>
                            
                            {template.use_cases && (
                              <div className="mb-3">
                                <p className="text-xs font-semibold text-gray-700 mb-1">Use Cases:</p>
                                <div className="flex flex-wrap gap-1">
                                  {template.use_cases.slice(0, 2).map((useCase, i) => (
                                    <Badge key={i} variant="secondary" className="text-xs">
                                      {useCase}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <div className="text-xs text-gray-500 mb-3">
                              <div className="mb-1">
                                <span className="font-semibold">Required:</span> {template.required_fields.slice(0, 3).join(', ')}
                                {template.required_fields.length > 3 && ` +${template.required_fields.length - 3} more`}
                              </div>
                            </div>
                            
                            {template.example_fields && (
                              <div className="bg-gray-50 rounded p-2 mb-3">
                                <p className="text-xs font-semibold text-gray-700 mb-1">Example:</p>
                                <div className="text-xs text-gray-600 space-y-0.5">
                                  {Object.entries(template.example_fields).slice(0, 2).map(([key, value]) => (
                                    <div key={key}>
                                      <span className="font-medium">{key}:</span> {value}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <Button 
                              size="sm" 
                              className="w-full"
                              onClick={() => {
                                toast.success(`${template.name} template selected! Upload a document to use it.`);
                              }}
                            >
                              <Zap className="w-4 h-4 mr-2" />
                              Use Template
                            </Button>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
        
        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {analytics && (
                <>
                  <motion.div variants={itemVariants}>
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Total Documents</p>
                            <p className="text-3xl font-bold">{analytics.total_documents}</p>
                          </div>
                          <FileText className="w-8 h-8 text-blue-500" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                  
                  <motion.div variants={itemVariants}>
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Avg Confidence</p>
                            <p className="text-3xl font-bold">{analytics.average_confidence}%</p>
                          </div>
                          <TrendingUp className="w-8 h-8 text-green-500" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                  
                  <motion.div variants={itemVariants}>
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">GPT-4 Vision</p>
                            <p className="text-3xl font-bold">
                              {analytics.by_extraction_method?.gpt4_vision || 0}
                            </p>
                          </div>
                          <Brain className="w-8 h-8 text-purple-500" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                  
                  <motion.div variants={itemVariants}>
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Recent (7d)</p>
                            <p className="text-3xl font-bold">{analytics.recent_activity}</p>
                          </div>
                          <Clock className="w-8 h-8 text-orange-500" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </>
              )}
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Documents by Category</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.by_category && (
                  <div className="space-y-3">
                    {Object.entries(analytics.by_category).map(([category, count]) => (
                      <div key={category} className="flex items-center justify-between">
                        <span className="text-sm font-medium capitalize">{category}</span>
                        <div className="flex items-center gap-2">
                          <Progress value={(count / analytics.total_documents) * 100} className="w-32" />
                          <span className="text-sm text-gray-500 min-w-12">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
        
        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Processing History</CardTitle>
                  <CardDescription>Your recently processed documents</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search documents..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 w-64"
                    />
                  </div>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium mb-1">No documents processed yet</p>
                  <p className="text-sm">Upload a document to get started</p>
                </div>
              ) : filteredHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium mb-1">No matching documents</p>
                  <p className="text-sm">Try a different search term</p>
                </div>
              ) : (
                <ScrollArea className="h-96">
                  <div className="space-y-3">
                    {filteredHistory.map((doc, index) => (
                    <motion.div
                      key={doc.document_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-medium">{doc.filename}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Badge variant="outline">{doc.document_type}</Badge>
                            <Badge variant={doc.extraction_method === 'gpt4_vision' ? 'default' : 'secondary'}>
                              {doc.extraction_method === 'gpt4_vision' ? 'GPT-4' : 'OCR'}
                            </Badge>
                            <span>{doc.field_count} fields</span>
                            <span>{doc.confidence}% confidence</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">
                          {new Date(doc.created_at).toLocaleDateString()}
                        </span>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </motion.div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Advanced Tab */}
        <TabsContent value="advanced">
          <Card>
            <CardHeader>
              <CardTitle>Advanced Features</CardTitle>
              <CardDescription>
                Additional tools and capabilities
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-2">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Table className="w-6 h-6 text-blue-500" />
                      <h3 className="font-semibold">Table Extraction</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Extract structured tables from documents with row and column detection
                    </p>
                    <Badge variant="secondary">GPT-4 Vision</Badge>
                  </CardContent>
                </Card>
                
                <Card className="border-2">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Shield className="w-6 h-6 text-green-500" />
                      <h3 className="font-semibold">ID Verification</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Verify identity documents with automated checks and validation
                    </p>
                    <Badge variant="secondary">5 Verification Checks</Badge>
                  </CardContent>
                </Card>
                
                <Card className="border-2">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Brain className="w-6 h-6 text-purple-500" />
                      <h3 className="font-semibold">Multi-Language OCR</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Support for 11 languages including English, Spanish, Chinese, Japanese
                    </p>
                    <Badge variant="secondary">11 Languages</Badge>
                  </CardContent>
                </Card>
                
                <Card className="border-2">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Zap className="w-6 h-6 text-yellow-500" />
                      <h3 className="font-semibold">Hybrid Extraction</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Combines GPT-4 Vision and Tesseract OCR for maximum accuracy
                    </p>
                    <Badge variant="secondary">95%+ Accuracy</Badge>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DocumentIntelligencePage;
